import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Mail, Phone, ArrowUpRight, Globe, Send, CheckCircle2, Linkedin, Instagram, Facebook } from "lucide-react";
import { toast } from "sonner";
import { SITE, API } from "@/lib/site";
import Seo from "@/components/seo/Seo";

const initialForm = {
  name: "",
  email: "",
  company: "",
  topic: "general",
  message: "",
  website: "", // honeypot
};

export default function Contact() {
  const { t } = useTranslation("pages");
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null); // {lead_id}

  const TOPICS = [
    { value: "general", label: t("contact.topics.general") },
    { value: "sales", label: t("contact.topics.sales") },
    { value: "partnership", label: t("contact.topics.partnership") },
    { value: "support", label: t("contact.topics.support") },
    { value: "security", label: t("contact.topics.security") },
    { value: "press", label: t("contact.topics.press") },
  ];

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    if (!form.name.trim() || form.name.trim().length < 2) return t("contact.errors.nameShort");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return t("contact.errors.invalidEmail");
    if (!form.message.trim() || form.message.trim().length < 10) return t("contact.errors.messageShort");
    return null;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { toast.error(err); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        const msg = typeof j?.detail === "string"
          ? j.detail
          : (j?.detail?.message || j?.message || `Something went wrong (HTTP ${res.status}).`);
        throw new Error(msg);
      }
      const j = await res.json();
      setSubmitted({ lead_id: j.lead_id });
      setForm(initialForm);
      toast.success("Message sent — we'll be in touch.");
    } catch (e2) {
      toast.error(e2.message || "Couldn't send your message. Try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Seo
        title={t("contact.seoTitle")}
        description={t("contact.seoDescription")}
        path="/contact"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "ContactPage",
            "url": "https://atlasforgefinancial.com/contact",
            "mainEntity": {
              "@type": "Organization",
              "name": "AtlasForge Financial",
              "email": "customersupport@atlasforgefinancial.com",
              "telephone": "+1-843-291-2556",
            },
          },
        ]}
      />
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 af-soft-glow pointer-events-none" />
        <div className="relative mx-auto max-w-6xl px-6 lg:px-10 pt-20 pb-16 md:pt-28">
          <p className="text-[12px] uppercase tracking-[0.18em] text-slate-500">{t("contact.eyebrow")}</p>
          <h1
            data-testid="contact-title"
            className="font-display mt-3 text-[44px] sm:text-[56px] lg:text-[64px] leading-[1.02] text-[#0A1128] max-w-3xl"
          >
            {t("contact.heroTitle")}
          </h1>
          <p className="mt-5 text-[17px] text-slate-600 max-w-2xl">
            {t("contact.heroBody")}
          </p>
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto max-w-6xl px-6 lg:px-10 pb-24 md:pb-28">
          <div className="grid lg:grid-cols-5 gap-6">
            {/* Left: contact form */}
            <div className="lg:col-span-3 rounded-3xl border border-slate-200 bg-white p-7 md:p-10 af-shadow-card">
              {submitted ? (
                <div data-testid="contact-form-success" className="py-6">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#E6F7EF]">
                    <CheckCircle2 className="h-5 w-5 text-[#1A4232]" />
                  </span>
                  <h2 className="font-display text-[28px] text-[#0A1128] mt-5">{t("contact.successTitle")}</h2>
                  <p className="mt-2 text-[15px] text-slate-600 max-w-md">
                    {t("contact.successBody")}
                  </p>
                  <p className="mt-4 text-[12.5px] text-slate-500">
                    {t("contact.successReference")} <span className="font-mono">{submitted.lead_id}</span>
                  </p>
                  <button
                    type="button"
                    data-testid="contact-form-send-another"
                    onClick={() => setSubmitted(null)}
                    className="mt-7 inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-5 py-2.5 text-[14px] font-medium text-[#0A1128] hover:border-[#0A1128] transition-colors"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form onSubmit={onSubmit} data-testid="contact-form" noValidate>
                  <p className="text-[12px] uppercase tracking-[0.18em] text-slate-500">{t("contact.formTitle")}</p>
                  <h2 className="font-display text-[26px] text-[#0A1128] mt-1.5">{t("contact.heroTitle")}</h2>

                  <div className="mt-7 grid sm:grid-cols-2 gap-5">
                    <Field label={t("contact.labels.name")} required>
                      <input
                        data-testid="contact-form-name"
                        type="text"
                        autoComplete="name"
                        required
                        value={form.name}
                        onChange={update("name")}
                        className={inputCls}
                        placeholder={t("contact.placeholders.name")}
                      />
                    </Field>
                    <Field label={t("contact.labels.email")} required>
                      <input
                        data-testid="contact-form-email"
                        type="email"
                        autoComplete="email"
                        required
                        value={form.email}
                        onChange={update("email")}
                        className={inputCls}
                        placeholder={t("contact.placeholders.email")}
                      />
                    </Field>
                    <Field label={t("contact.labels.company")}>
                      <input
                        data-testid="contact-form-company"
                        type="text"
                        autoComplete="organization"
                        value={form.company}
                        onChange={update("company")}
                        className={inputCls}
                        placeholder={t("contact.placeholders.company")}
                      />
                    </Field>
                    <Field label={t("contact.labels.topic")}>
                      <select
                        data-testid="contact-form-topic"
                        value={form.topic}
                        onChange={update("topic")}
                        className={inputCls + " appearance-none bg-white pr-9"}
                      >
                        {TOPICS.map((t) => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                    </Field>
                  </div>

                  <div className="mt-5">
                    <Field label={t("contact.labels.message")} required>
                      <textarea
                        data-testid="contact-form-message"
                        required
                        value={form.message}
                        onChange={update("message")}
                        rows={6}
                        className={inputCls + " resize-y min-h-[140px]"}
                        placeholder={t("contact.placeholders.message")}
                      />
                    </Field>
                  </div>

                  {/* Honeypot — hidden from sighted users + screen readers */}
                  <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", opacity: 0, pointerEvents: "none", height: 0, overflow: "hidden" }}>
                    <label>
                      Website
                      <input
                        type="text"
                        name="website"
                        tabIndex={-1}
                        autoComplete="off"
                        value={form.website}
                        onChange={update("website")}
                      />
                    </label>
                  </div>

                  <div className="mt-7 flex items-center justify-between gap-4 flex-wrap">
                    <p className="text-[12px] text-slate-500 max-w-sm">
                      By submitting, you agree to our <a href="/legal/privacy" className="underline hover:text-[#0A1128]">Privacy Policy</a>. We never sell your data.
                    </p>
                    <button
                      data-testid="contact-form-submit"
                      type="submit"
                      disabled={submitting}
                      className="inline-flex items-center gap-2 rounded-full bg-[#0A1128] text-white px-6 py-3 text-[14px] font-medium hover:bg-[#1A4232] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {submitting ? t("contact.sending") : <>{t("contact.submit")} <Send className="h-4 w-4" /></>}
                    </button>
                  </div>
                </form>
              )}
            </div>

            {/* Right: direct contact + socials */}
            <div className="lg:col-span-2 flex flex-col gap-5">
              <a
                href={`mailto:${SITE.email}`}
                data-testid="contact-email-card"
                className="group rounded-3xl border border-slate-200 bg-white p-7 hover:-translate-y-1 transition-transform af-shadow-card"
              >
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#EFF6FF]">
                  <Mail className="h-5 w-5 text-[#2563EB]" />
                </span>
                <p className="text-[12px] uppercase tracking-[0.18em] text-slate-500 mt-5">Email</p>
                <p className="font-display mt-1.5 text-[#0A1128] text-[clamp(13px,3vw,20px)] whitespace-nowrap" data-testid="contact-email-text">{SITE.email}</p>
                <p className="mt-2 text-[13.5px] text-slate-600">For customer support, accounts, and general inquiries.</p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-medium text-[#0A1128] group-hover:gap-2.5 transition-all">
                  Send a message <ArrowUpRight className="h-4 w-4" />
                </span>
              </a>

              <a
                href={`tel:${SITE.phone.replace(/[^0-9]/g, "")}`}
                data-testid="contact-phone-card"
                className="group rounded-3xl border border-slate-200 bg-[#0A1128] text-white p-7 hover:-translate-y-1 transition-transform af-shadow-card"
              >
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-white/10">
                  <Phone className="h-5 w-5" />
                </span>
                <p className="text-[12px] uppercase tracking-[0.18em] text-slate-400 mt-5">Phone</p>
                <p className="font-display text-[24px] md:text-[26px] mt-1.5">{SITE.phone}</p>
                <p className="mt-2 text-[13.5px] text-slate-300">Speak with a member of the AtlasForge team.</p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-medium text-[#FF8E73] group-hover:gap-2.5 transition-all">
                  Call now <ArrowUpRight className="h-4 w-4" />
                </span>
              </a>

              <div className="rounded-3xl border border-slate-200 bg-[#F7F9FC] p-7">
                <p className="text-[12px] uppercase tracking-[0.18em] text-slate-500">Follow AtlasForge</p>
                <div className="mt-4 flex items-center gap-3" data-testid="contact-socials">
                  <SocialLink href={SITE.socials.linkedin} label="LinkedIn" testid="contact-social-linkedin" Icon={Linkedin} />
                  <SocialLink href={SITE.socials.instagram} label="Instagram" testid="contact-social-instagram" Icon={Instagram} />
                  <SocialLink href={SITE.socials.facebook} label="Facebook" testid="contact-social-facebook" Icon={Facebook} />
                </div>
              </div>
            </div>
          </div>

          <div className="mt-10 rounded-2xl border border-slate-200 bg-[#F7F9FC] p-6 md:p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-start gap-3">
              <Globe className="h-5 w-5 text-slate-500 mt-1" />
              <div>
                <p className="font-display text-[16px] text-[#0A1128]">AtlasForge Financial</p>
                <p className="text-[13.5px] text-slate-600">South Carolina · United States</p>
              </div>
            </div>
            <p className="text-[12.5px] text-slate-500 max-w-md">
              For security disclosures, please include reproduction steps and supporting evidence in your message.
            </p>
          </div>
        </div>
      </section>
    </>
  );
}

const inputCls =
  "w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-[14.5px] text-[#0A1128] placeholder:text-slate-400 outline-none focus:border-[#0A1128] focus:ring-2 focus:ring-[#0A1128]/10 transition-colors";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-[12px] font-medium text-slate-600">
        {label}{required && <span className="text-[#FF5A36]"> *</span>}
      </span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

function SocialLink({ href, label, testid, Icon }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`AtlasForge Financial on ${label}`}
      data-testid={testid}
      className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 hover:text-[#0A1128] hover:border-[#0A1128] transition-colors"
    >
      <Icon className="h-4 w-4" />
    </a>
  );
}
